/**
 * JourneyTimeline v32 — Reference-match (final proportioner)
 * "Small bike, big world" — ét sammenhængende ultra-bredt panorama
 *
 * LAG 2: Timeline container  (STRIP_H, position:absolute bottom)
 * LAG 3: Landskabs-bånd      (LAND_H, JPG baggrund, top-fade)
 * LAG 4: MC overlay          (mc_side_view.png, lille mod verdenen)
 * LAG 5: Grøn GPS-linje      (#2DFF88, ALTID synlig)
 * LAG 6: Pins + milepæle     (stikker op over strip)
 * LAG 7: 3-kolonne Playback UI
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getActivePanorama, getPanoramaCSS } from '../../config/panoramaLibrary';

// ── Konstanter — reference-match (IMG_8139 — iPhone screenshot) ──────────
const STRIP_H  = 158;   // 108 panorama + 50 kontrolbar (øget for bedre spacing)
const LAND_H   = 108;   // panoramisk landscapebånd — himmel + bakker + asfalt
const CTRL_H   = 50;    // øget fra 46 → 50 for bedre luft og premium feel
const LAND_TOP = 0;
const SVG_H    = LAND_H;

// GPS_BASE ved 89% af 104 = 93 — hjul PÅ vejbanen (road zone y=90-104)
const GPS_BASE = Math.round(LAND_H * 0.895);  // 93

const LAND_W = 1264;   // bredde på side-scroll panorama (1264×848px native — ingen stræk)

// MC — skaleret til reference: ~57% af LAND_H (integreret i landskabet, ikke dominerende)
const MC_SRC     = '/assets/mc_side_view.png';
const MC_FRAC    = 0.38;
const MC_H       = 60;                               // P1: –17% fra 72 → motorcyklen er del af verdenen
const MC_W       = Math.round(MC_H * 1.72);          // ≈ 103px
const WHEEL_FRAC  = 0.88;
const SHOW_MC    = true;
const MC_TOP     = GPS_BASE - Math.round(MC_H * WHEEL_FRAC);  // 86 − 63 = 23px

// ── Panorama baggrundsbillede — hentes fra Panorama Library ──────────────────
// Skift aktivt panorama i /src/config/panoramaLibrary.js (ACTIVE_PANORAMA_ID).
// JourneyTimeline ændres aldrig for at skifte panorama.
// REGLER: Ingen filtre, ingen overlays, ingen gradients, ingen blur — 1:1 visning.
const _ACTIVE_PANO = getActivePanorama();
const BG_PANORAMA  = getPanoramaCSS(_ACTIVE_PANO);
const PANO_SRC     = _ACTIVE_PANO.file;

const ANIM_CSS = `
@keyframes jt-pulse{0%,100%{opacity:1}50%{opacity:.35}}
@keyframes mc-bounce{0%,100%{transform:translateY(0px)}50%{transform:translateY(-0.5px)}}
@keyframes pin-pop{
  0%  {transform:scale(.65,.65);opacity:0}
  55% {transform:scale(1.09,1.11);opacity:1}
  78% {transform:scale(.96,.97);opacity:1}
  100%{transform:scale(1,1);opacity:1}
}
@keyframes pin-glow-ring{
  0%  {transform:scale(1);  opacity:0}
  18% {opacity:.80}
  100%{transform:scale(2.6);opacity:0}
}
.pin-activating {
  animation: pin-pop 0.52s cubic-bezier(0.34,1.56,0.64,1) forwards;
  transform-box: fill-box;
  transform-origin: 50% 100%;
}
.pin-glow-ring {
  animation: pin-glow-ring 0.95s ease-out 0.25s 1 forwards;
  transform-box: fill-box;
  transform-origin: 50% 50%;
  opacity: 0;
}
`;

// ── GPS bølgelinje — dæmpet amplitude for blød, tung kørsel ─────────────────
function gY(wx) {
  return GPS_BASE
    + Math.sin(wx * 0.0194 + 0.517) * 2.6
    + Math.sin(wx * 0.0487 + 1.443) * 1.7
    + Math.sin(wx * 0.1128 + 2.091) * 0.9
    + Math.sin(wx * 0.2437 + 3.877) * 0.5
    + Math.sin(wx * 0.0833 + 0.923) * 1.1
    + Math.sin(wx * 0.1547 + 4.231) * 0.3;
}

function buildGPS(x0, x1, bgOffset) {
  let d = '';
  const end = Math.ceil(x1);
  for (let x = Math.floor(x0); x <= end; x += 2) {
    const y = gY(x + bgOffset).toFixed(1);
    d += d ? ` L${x} ${y}` : `M${x} ${y}`;
  }
  return d;
}

function fmtClock(ms) {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ── Pin farver ────────────────────────────────────────────────────────────────
const PIN_COLORS = {
  photo:   '#2DFF88',
  video:   '#44AAFF',
  club:    '#FF8C30',
  pitstop: '#FFD020',
  sleep:   '#9944FF',
};

// ── Pin ikoner — premium iOS-kvalitet, skaleret til 42×40px boks ─────────────
function PinIcon({ type, cx, cy, col }) {
  if (type === 'photo') return (
    <g>
      {/* Kamera body */}
      <rect x={cx - 12} y={cy - 7} width={24} height={16} rx="2.5"
        fill="none" stroke={col} strokeWidth="1.9"/>
      {/* Linse */}
      <circle cx={cx} cy={cy + 1} r="5.2" fill="none" stroke={col} strokeWidth="1.9"/>
      <circle cx={cx} cy={cy + 1} r="2.3" fill={col}/>
      {/* Viewfinder bump */}
      <rect x={cx - 4} y={cy - 11} width={8} height={4.5} rx="1.3" fill={col}/>
      {/* Flash */}
      <circle cx={cx + 9} cy={cy - 7} r="1.4" fill={col} opacity="0.75"/>
    </g>
  );
  if (type === 'video') return (
    <g>
      {/* Stor, tydelig play-trekant */}
      <polygon
        points={`${cx - 9},${cy - 11} ${cx - 9},${cy + 11} ${cx + 12},${cy}`}
        fill={col}
      />
    </g>
  );
  if (type === 'club') return (
    <text x={cx} y={cy + 5} textAnchor="middle" fill={col}
      fontSize="14" fontWeight="900" fontFamily="system-ui,sans-serif"
      letterSpacing="-0.5">MC</text>
  );
  if (type === 'pitstop') return (
    <g>
      {/* Benzinpumpe body */}
      <rect x={cx - 8} y={cy - 10} width={13} height={21} rx="2"
        fill="none" stroke={col} strokeWidth="1.9"/>
      {/* Nozzle arm */}
      <path d={`M${cx + 5},${cy - 10} Q${cx + 13},${cy - 7} ${cx + 13},${cy - 1} Q${cx + 13},${cy + 4} ${cx + 8},${cy + 4}`}
        fill="none" stroke={col} strokeWidth="1.9"/>
      {/* Brændstofskærm */}
      <rect x={cx - 6.5} y={cy - 6} width={9} height={5.5} rx="1"
        fill={col} opacity="0.45"/>
    </g>
  );
  if (type === 'sleep') return (
    <g>
      {/* Sengebund */}
      <rect x={cx - 13} y={cy + 3} width={26} height={9} rx="2"
        fill="none" stroke={col} strokeWidth="1.9"/>
      {/* Sengehoved */}
      <rect x={cx - 13} y={cy - 10} width={10} height={13} rx="2"
        fill="none" stroke={col} strokeWidth="1.9"/>
      {/* Pude indikation */}
      <rect x={cx - 11} y={cy - 8} width={6} height={5} rx="1"
        fill={col} opacity="0.40"/>
    </g>
  );
  return <circle cx={cx} cy={cy} r="7" fill={col}/>;
}

// ── Målflag SVG ───────────────────────────────────────────────────────────────
function FlagAt({ sx, sy }) {
  const ph   = 28;
  const fw   = 20;
  const fh   = 13;
  const cs   = 3.25;
  const cols = Math.round(fw / cs);
  const rows = Math.round(fh / cs);
  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      cells.push(
        <rect key={`f${r}${c}`}
          x={sx + c * cs} y={sy - ph - fh + r * cs}
          width={cs + 0.1} height={cs + 0.1}
          fill={(r + c) % 2 === 0 ? 'rgba(248,248,248,0.98)' : 'rgba(10,10,10,0.98)'}
        />
      );
    }
  }
  return (
    <g>
      <line x1={sx} y1={sy} x2={sx} y2={sy - ph - fh}
        stroke="rgba(255,255,255,0.80)" strokeWidth="1.4" strokeLinecap="round"/>
      <rect x={sx + 0.8} y={sy - ph - fh + 0.8} width={fw} height={fh}
        fill="rgba(0,0,0,0.25)" rx="0.5"/>
      {cells}
      <rect x={sx} y={sy - ph - fh} width={fw} height={fh}
        fill="none" stroke="rgba(255,255,255,0.20)" strokeWidth="0.6"/>
      <circle cx={sx} cy={sy} r="2.2" fill="rgba(255,255,255,0.65)"/>
    </g>
  );
}

// ── Komponent ─────────────────────────────────────────────────────────────────
function JourneyTimelineInner({
  progress     = 0,
  elapsedMs    = 0,
  totalMs      = 0,
  onSeek,
  onScrubMove, // P4: real-time 1:1 scrub callback (lettere end onSeek — ingen audio-sync)
  onPinClick,  // (ms: {progress, type, url?, thumb?}) → kald fra RideReplayPlayer ved pin-tryk
  testId,
  mediaPlaying = false,
  milestones   = [],
  rideMedia,
  nearbyClubs,
  riderName    = '',   // "Video lavet af: X" i midterkolonnen
  // backward-compat
  bgTheme, paused, onPlayPause, rawPoints, route, // eslint-disable-line no-unused-vars
}) {
  const [drag, setDrag]   = useState(false);
  const [scrub, setScrub] = useState(null);
  const [W, setW]         = useState(366);
  const ref    = useRef(null);
  const rafRef = useRef(null); // P4: RAF-ref til throttlet real-time scrub
  const uid = testId || 'jt';
  // Ref til msList — opdateres efter useMemo, så onDown altid kan læse
  // aktuel liste uden at genopbygge callback ved hver ændring.
  const msListRef = useRef([]);
  // ── Pin-aktiverings-tracking: opdages live → vises med unfold-animation ──
  const prevLiveRef = useRef(-1);
  const pinActivRef = useRef({}); // { pinIdx: timestampMs }

  const live   = scrub != null ? scrub : progress;
  const liveMs = scrub != null ? Math.round(scrub * (totalMs || 0)) : (elapsedMs || 0);

  // ── Spor pin-aktiveringer (side-effect-fri ref-opdatering i render) ──────
  const nowTs = Date.now();
  msListRef.current.forEach((ms, i) => {
    if (ms.type === 'flag') return;
    if (live >= ms.progress) {
      if (prevLiveRef.current < ms.progress && !pinActivRef.current[i]) {
        pinActivRef.current[i] = nowTs;
      }
    } else {
      // Scrub tilbage → nulstil animation så den kan spilles igen
      if (pinActivRef.current[i]) delete pinActivRef.current[i];
    }
  });
  prevLiveRef.current = live;


  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => setW(e.contentRect.width || 366));
    obs.observe(el);
    setW(el.offsetWidth || 366);
    return () => obs.disconnect();
  }, []);

  // P2: Preload det godkendte panorama øjeblikkeligt ved mount —
  // ingen tom baggrund nogensinde, selv ved langsom forbindelse.
  useEffect(() => {
    const img = new window.Image();
    img.src = PANO_SRC;
  }, []);

  const onDown = useCallback((e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const clickX = e.clientX - r.left;

    // ── Pin-klik detektion ────────────────────────────────────────────
    if (onPinClick) {
      const PIN_HIT_PX = 22;
      const hit = msListRef.current.find(ms =>
        ms.type !== 'flag' &&
        Math.abs(ms.progress * r.width - clickX) < PIN_HIT_PX
      );
      if (hit) {
        onPinClick(hit);
        return;
      }
    }

    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag(true);
    const val = Math.max(0, Math.min(1, clickX / r.width));
    setScrub(val);
    // Øjeblikkelig seek ved tap
    if (onScrubMove) onScrubMove(val);
  }, [onPinClick, onScrubMove]);

  // P4: Real-time 1:1 scrubbing — kalder onScrubMove via RAF for nøjagtig
  // finger-tracking uden at overbelaste React-render-loopet.
  const onMove = useCallback((e) => {
    if (!drag || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const val = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    setScrub(val);
    if (onScrubMove) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => { onScrubMove(val); });
    }
  }, [drag, onScrubMove]);

  const onUp = useCallback((e) => {
    if (!drag) return;
    setDrag(false);
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (scrub != null && onSeek) onSeek(scrub);
    setScrub(null);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /**/ }
  }, [drag, scrub, onSeek]);

  const onCancel = useCallback(() => {
    setDrag(false);
    setScrub(null);
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  // ── MC + GPS matematik ────────────────────────────────────────────────────
  const mcX    = Math.round(live * W);
  const rawVis = Math.min(Math.round(W * MC_FRAC), mcX);
  const flagX  = W - 22;

  // ── Punkt 5: Cinematisk afslutning ───────────────────────────────────────
  // I de sidst 25% bremser MC gradvist op og forbliver næsten stationær
  // på skærmen mens panoramaet skyller forbi — "kamera følger MC"-effekt.
  // MC ender ved rawVis (~38% fra venstre). Ingen rush mod flagget.
  // easeOutCubic: hurtig start → blød stop (1 - (1-t)³).
  const CINEMATIC_START = 0.75;
  let visX;
  if (live <= CINEMATIC_START) {
    visX = rawVis;
  } else {
    const t    = Math.min(1, (live - CINEMATIC_START) / (1 - CINEMATIC_START));
    const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic
    // MC driver meget langsomt fra rawVis mod maks 5% til højre (umærkelig drift)
    const targetX = Math.min(rawVis + Math.round(W * 0.05), flagX - 30);
    visX = Math.round(rawVis + ease * (targetX - rawVis));
  }

  const bgScrl  = Math.round(live * Math.max(0, LAND_W - W));
  const mcY       = gY(visX + bgScrl);

  // ── Perspektiv-skalering: MC lidt mindre ved start/slut ─────────────────────
  // Kurve: 0.82 ved 0%, 1.0 ved 50%, 0.82 ved 100%
  const mcScale  = 0.82 + 0.18 * Math.sin(Math.PI * live);
  const sMcW     = Math.round(MC_W * mcScale);
  const sMcH     = Math.round(MC_H * mcScale);

  // ── MC safe-X: sikrer MC aldrig klippes ved kanter ───────────────────────
  // visX bruges til GPS-linjen. mcVisX bruges KUN til MC-billedets position.
  const mcVisX = Math.max(
    Math.round(sMcW * 0.45),                    // venstre: MC's venstre kant ≥ 0
    Math.min(W - Math.round(sMcW * 0.55), visX) // højre:   MC's højre kant ≤ W
  );

  const gGreen = useMemo(() => buildGPS(0, visX, bgScrl), [visX, bgScrl]);
  const gDim   = useMemo(() => buildGPS(visX, W, bgScrl), [visX, W, bgScrl]);

  // ── Målflag X: animeres mod MC under cinematisk afslutning ────────────────
  // I de sidst 25% glider flaget fra højre kant ind og møder MC's GPS-prik.
  let flagRenderX = W - 18;
  if (live >= CINEMATIC_START) {
    const cinT    = Math.min(1, (live - CINEMATIC_START) / (1 - CINEMATIC_START));
    const cinEase = 1 - Math.pow(1 - cinT, 3); // easeOutCubic
    flagRenderX   = Math.round((W - 18) + cinEase * ((visX + 8) - (W - 18)));
  }

  // ── Milestone-liste ───────────────────────────────────────────────────────
  const msList = useMemo(() => {
    const items = [];
    rideMedia?.forEach(m => {
      if (m?.position_progress != null && m?.url)
        items.push({
          progress: m.position_progress,
          type: m.type === 'video' ? 'video' : 'photo',
          url: m.url,
          thumb: m.thumbnail_url || m.url,
        });
    });
    nearbyClubs?.forEach(c => {
      const p = c.entry_progress ?? c.position_progress;
      if (p != null) items.push({ progress: p, type: 'club' });
    });
    items.push({ progress: 1.0, type: 'flag' });
    items.sort((a, b) => a.progress - b.progress);

    if (items.length <= 1) {
      // Ingen rigtige pins → vis kun flaget (ingen falske demo-pins).
      return milestones.length > 0
        ? [...milestones, { progress: 1.0, type: 'flag' }]
        : [{ progress: 1.0, type: 'flag' }];
    }

    const deduped = [];
    for (const ms of items) {
      if (!deduped.find(g => Math.abs(g.progress - ms.progress) < 0.025))
        deduped.push(ms);
    }
    return deduped;
  }, [rideMedia, nearbyClubs, milestones]);
  // Sync ref efter msList er beregnet — bruges i onDown (undgår closure-stale)
  msListRef.current = msList;

  // Tre klare niveauer: LÅV (L), MIDDEL (M), HØJ (H) — skaleret til LAND_H=160
  // GPS_BASE=133, IH=44. Formel: stemH = GPS_BASE − tip_offset − IH
  // L=91  → pin-boks toppen 2px over strip-kanten
  // M=125 → pin-boks 36px over strip-kanten → over panoramaet
  // H=165 → pin-boks 76px over strip-kanten → bryder op i kortvisningen
  const PIN_STEMS = [58, 80, 106, 58, 80, 106, 58, 106];  // skaleret ~0.65x fra [91,125,165]

  const IW = 32;   // skaleret fra 46 (~0.70x)
  const IH = 30;   // skaleret fra 44 (~0.68x)

  return (
    <div
      ref={ref}
      data-testid={testId || 'journey-timeline'}
      style={{
        position: 'absolute',
        left: 0, right: 0,
        // FIX: Brug max(8px, ...) så timeline aldrig forsvinder under skærmen
        // på devices uden home indicator (safe-area ≈ 0)
        bottom: 'max(8px, calc(-18px + env(safe-area-inset-bottom, 0px)))',
        height: mediaPlaying ? LAND_H : STRIP_H,
        overflow: 'visible',
        zIndex: 88,
        cursor: drag ? 'grabbing' : 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        // P3: Hele containeren fader til opacity:0 under media.
        // Videoen/billedet fylder hele skærmen — ingen overlay.
        opacity: mediaPlaying ? 0 : 1,
        // Container er TRANSPARENT — ingen boks-effekt, ingen kanter.
        background: 'transparent',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        transition: 'opacity 600ms ease-in-out, height 400ms cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: mediaPlaying ? 'none' : 'auto',
      }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onCancel}
    >
      <style>{ANIM_CSS}</style>

      {/* ══ LAG 3: LANDSKABS-BÅND ════════════════════════════════════════
          PANORAMAET VISES 1:1 — INGEN alpha-maske, INGEN filter,
          INGEN overlay, INGEN gradient, INGEN blur, INGEN CSS-filter.
          
          Overgangen fra kort → panorama håndteres i RideReplayPlayer:
          - En sky-farvet baggrund matcher panoramaets top
          - Kortets bund fadeer ud (maskImage på kortets wrapper)
          - Panoramaet selv er helt uændret og fuldt synligt
          
          Under media (mediaPlaying=true): HELE container fader til
          opacity:0 — panoramaet og alle elementer forsvinder.
      ═══════════════════════════════════════════════════════════════════ */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: LAND_TOP,
        height: LAND_H,
        overflow: 'hidden',
        zIndex: 2,
        pointerEvents: 'none',
        // Blød alpha-fade øverst (~62px) → kortet smelter ned i panoramaet
        // uden synlig vandret linje. KUN transparens, ingen mørk farve/filter.
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 60%)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 60%)',
      }}>
        {/* Panorama 1:1 — INGEN filter, INGEN farveændring, INGEN overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: BG_PANORAMA,
          backgroundSize: `${LAND_W}px ${LAND_H}px`,
          backgroundRepeat: 'no-repeat',
          backgroundPositionX: `-${bgScrl}px`,
          backgroundPositionY: '0',
        }} />
      </div>

      {/* ══ LAG 7: KONTROLBAR ════════════════════════════════════════════
          Betinget rendering — FJERNES FRA DOM under media (krav 3).
          Glass-morfisme kun på kontrolbaren (ikke container) — ét visuelt element.
          Ingen afrundede hjørner øverst — panoramaet løber direkte ind i glasset.
      ═══════════════════════════════════════════════════════════════════ */}
      {!mediaPlaying && (
        <div style={{
          position: 'absolute', left: 0, right: 0,
          bottom: 0, height: CTRL_H,
          // Tim's brief Jul 12, 2026 — INGEN mørk overlay/gradient.
          // Kontrolbaren er helt transparent; teksten står skarpt via text-shadow.
          background: 'transparent',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          display: 'flex', alignItems: 'center',
          zIndex: 8,
        }}>
        {/* ── VENSTRE: Play + Tid ── */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', paddingLeft: 8, gap: 5, minWidth: 0 }}>
          {onPlayPause && (
            <div
              onClick={(e) => { e.stopPropagation(); onPlayPause(); }}
              style={{
                width: 28, height: 28, borderRadius: '50%',
                border: '1.75px solid #2DFF88',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0, pointerEvents: 'auto',
              }}
            >
              <span style={{ fontSize: 10, color: '#2DFF88', marginLeft: paused ? 1 : 0 }}>
                {paused ? '▶' : '▐▐'}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, minWidth: 0 }}>
            <span
              data-testid={`${uid}-elapsed`}
              style={{
                fontSize: 14, fontWeight: 700, color: '#2DFF88',
                fontFamily: 'monospace', letterSpacing: '0.04em',
                textShadow: '0 1px 6px rgba(0,0,0,0.9)', flexShrink: 0,
              }}
            >
              {fmtClock(liveMs)}
            </span>
            {totalMs > 0 && (
              <span style={{
                fontSize: 11, color: 'rgba(255,255,255,0.50)',
                fontFamily: 'monospace', flexShrink: 0,
              }}>
                / {fmtClock(totalMs)}
              </span>
            )}
          </div>
        </div>

        {/* ── MIDT: Video lavet af ── */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {riderName ? (
            <span style={{
              fontSize: 11, color: 'rgba(255,255,255,0.92)',
              letterSpacing: '0.04em', textAlign: 'center',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              maxWidth: '100%', padding: '0 6px',
              fontFamily: "system-ui,-apple-system,sans-serif",
              fontWeight: 600,
              textShadow: '0 1px 5px rgba(0,0,0,0.75)',
            }}>
              Video lavet af:{' '}
              <span style={{ color: '#2DFF88', fontWeight: 700 }}>{riderName}</span>
            </span>
          ) : null}
        </div>

        {/* ── HØJRE: ≡ BAREMC ≡ ── */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 1, textShadow: '0 1px 5px rgba(0,0,0,0.75)' }}>
            <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 8, fontWeight: 700 }}>≡ </span>
            <span style={{ fontSize: 13, fontWeight: 900, color: 'rgba(255,255,255,0.96)', letterSpacing: '0.13em' }}>BARE</span>
            <span style={{ fontSize: 13, fontWeight: 900, color: '#2DFF88', letterSpacing: '0.13em' }}>MC</span>
            <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 8, fontWeight: 700 }}> ≡</span>
          </div>
        </div>
      </div>
      )}

      {/* ══ LAG 5: GPS-LINJE — ALTID SYNLIG ═════════════════════════════
          Grøn bølge, scrub-track. Under media: ingen translate —
          ruten sidder naturligt i panoramaet (vejbane-position).
      ═══════════════════════════════════════════════════════════════════ */}
      <svg
        style={{
          position: 'absolute', top: 0, left: 0,
          width: '100%', height: SVG_H,
          zIndex: 16, overflow: 'visible', pointerEvents: 'none',
          transform: 'translateY(0)',
        }}
        viewBox={`0 0 ${W} ${SVG_H}`}
        preserveAspectRatio="none"
      >
        <defs>
          {/* P5: Stærkere glow — tre blur-passes → +40% lysere selvlysende grøn */}
          <filter id={`gw-${uid}`} x="-30%" y="-500%" width="160%" height="1100%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="glow"/>
            <feMerge>
              <feMergeNode in="glow"/>
              <feMergeNode in="glow"/>
              <feMergeNode in="glow"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Fremtidig rute — diskret hvid (skjult under media) */}
        {!mediaPlaying && (
          <path d={gDim}
            stroke="rgba(255,255,255,0.16)" strokeWidth="1.4"
            fill="none" strokeLinecap="round"/>
        )}

        {/* Tilbagelagt — P5: +25% tykkere (2.25 fra 1.8) selvlysende grøn linje */}
        {visX > 2 && (
          <path d={gGreen}
            stroke="#2DFF88" strokeWidth="2.25"
            fill="none" strokeLinecap="round"
            filter={`url(#gw-${uid})`}/>
        )}

        {!mediaPlaying && (
          <circle cx="3" cy={gY(bgScrl + 3)} r="3" fill="#2DFF88" opacity="0.55"/>
        )}

        {/* Målflag — sidst 25% af turen (cinematisk afslutning) */}
        {!mediaPlaying && live >= CINEMATIC_START && (
          <g opacity={Math.min(1, (live - CINEMATIC_START) / 0.06)}>
            <FlagAt sx={flagRenderX} sy={gY(flagRenderX + bgScrl)}/>
          </g>
        )}

        {/* Cursor punkt — kun under mediaPlaying */}
        {mediaPlaying && (
          <g>
            <circle cx={visX} cy={mcY} r="7"
              fill="rgba(45,255,136,0.28)"
              style={{ animation: 'jt-pulse 1.4s ease-in-out infinite' }}/>
            <circle cx={visX} cy={mcY} r="3.5" fill="#2DFF88"/>
            <circle cx={visX} cy={mcY} r="1.8" fill="#fff"/>
          </g>
        )}

        {!mediaPlaying && (
          <g>
            {/* P5: GPS position-prik — større og mere eksklusive */}
            <circle cx={visX} cy={mcY} r="13" fill="rgba(45,255,136,0.08)"/>
            <circle cx={visX} cy={mcY} r="8" fill="rgba(45,255,136,0.38)"/>
            <circle cx={visX} cy={mcY} r="5" fill="#2DFF88" filter={`url(#gw-${uid})`}/>
          </g>
        )}
      </svg>

      {/* ══ LAG 4: MC OVERLAY ════════════════════════════════════════════
          MC er ALTID synlig i panoramaet. Ingen translateY under media —
          MC sidder naturligt på vejbanen i panoramastripen.
      ═══════════════════════════════════════════════════════════════════ */}
      {SHOW_MC && (
        <div style={{
          position: 'absolute',
          left: mcVisX - Math.round(sMcW * 0.45),
          top: Math.round(mcY - sMcH * WHEEL_FRAC),
          width: sMcW,
          height: sMcH,
          zIndex: 18,
          pointerEvents: 'none',
          transform: 'translateY(0px)',
        }}>
          <img
            src={MC_SRC}
            alt=""
            style={{
              width: '100%', height: '100%',
              objectFit: 'contain',
              objectPosition: 'center bottom',
              display: 'block',
              animation: 'mc-bounce 1.4s ease-in-out infinite',
              filter: 'drop-shadow(0 0 5px rgba(255,255,255,0.40)) drop-shadow(0 3px 9px rgba(0,0,0,0.55)) brightness(1.08)',
            }}
          />
        </div>
      )}

      {/* ══ LAG 6: PINS ════════════════════════════════════════════════════
          Under media-afspilning: SKJULT (opacity:0).
          Bruger krav: ingen pins, ingen markører, ingen tidsangivelser
          når et billede/video afspilles.
      ═══════════════════════════════════════════════════════════════════ */}
      <svg
        style={{
          position: 'absolute', top: 0, left: 0,
          width: '100%', height: SVG_H,
          overflow: 'visible',
          pointerEvents: mediaPlaying ? 'none' : undefined,
          zIndex: 20,
          opacity: mediaPlaying ? 0 : 1,
          transition: 'opacity 300ms ease-out',
          transform: 'translateY(0px)',
        }}
        viewBox={`0 0 ${W} ${SVG_H}`}
        preserveAspectRatio="none"
      >
        <defs>
          <filter id={`gp-${uid}`} x="-25%" y="-300%" width="150%" height="700%">
            <feGaussianBlur stdDeviation="1.3" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {msList.map((ms, i) => {
          if (ms.type === 'flag') return null;

          const sx = Math.round(ms.progress * W);
          if (sx < -60 || sx > W + 60) return null;

          const sy   = gY(sx + bgScrl);
          const done = live >= ms.progress;
          const col  = done ? (PIN_COLORS[ms.type] || '#2DFF88') : 'rgba(110,120,110,0.45)';

          const pinOpacity = done
            ? Math.max(0, 1 - (live - ms.progress) / 0.07)
            : 1.0;

          const stemH = PIN_STEMS[i % PIN_STEMS.length];
          const iconY = sy - stemH - IH;

          // ── Pin safe-X: samme mønster som mcVisX ─────────────────────────
          // pinX: rendering-center for pin-boks, thumbnail og ikon.
          // labelX: rendering-center for timestamp-label.
          // sx beholdes til stamnedpoint og GPS-prik (den rigtige GPS-position).
          const HALF_IW = Math.ceil(IW / 2);           // 23px — halv pinboks-bredde
          const HALF_TS = 16;                           // halv timestamp-label-bredde (32px)
          const pinX   = Math.max(HALF_IW, Math.min(W - HALF_IW, sx));
          const labelX = Math.max(HALF_TS, Math.min(W - HALF_TS, sx));

          const cx = pinX;
          const cy = iconY + IH / 2;

          const tsMs   = Math.round(ms.progress * (totalMs || 0));
          const tsText = totalMs > 0 ? fmtClock(tsMs) : '';

          // Thumbnail: brug url fra rideMedia hvis tilgængeligt
          const hasThumb = (ms.type === 'photo' || ms.type === 'video') && ms.thumb;
          const thumbSrc = hasThumb
            ? (ms.thumb?.startsWith('http') ? ms.thumb : `${process.env.REACT_APP_BACKEND_URL}${ms.thumb}`)
            : null;
          const clipId = `clip-${uid}-${i}`;

          const activTs = pinActivRef.current[i];
          const isActivating = !!activTs && (nowTs - activTs) < 850;

          return (
            <g key={i} opacity={pinOpacity}>
              {/* Stam: bund ved sx (GPS-position), top ved pinX (clamped) */}
              <line x1={sx} y1={sy} x2={pinX} y2={iconY + IH}
                stroke={col} strokeWidth="1.2" opacity={done ? 0.75 : 0.25}
                strokeLinecap="round"/>

              {/* GPS-prik: altid ved sx (den rigtige GPS-position) */}
              <circle cx={sx} cy={sy} r="5" fill={col} opacity={done ? 0.18 : 0.05}/>
              <circle cx={sx} cy={sy} r="3" fill={col}
                filter={done ? `url(#gp-${uid})` : undefined}/>
              <circle cx={sx} cy={sy} r="1.5" fill="rgba(255,255,255,0.90)"/>

              {/* Pulse-ring: spilles én gang når pin aktiveres */}
              {isActivating && (
                <circle cx={sx} cy={sy} r="6"
                  fill="none" stroke={col} strokeWidth="1.5"
                  className="pin-ring" />
              )}

              {/* Pin-boks + label: animeret ved aktivering */}
              <g className={isActivating ? 'pin-activating' : undefined}
                 style={{ transformBox: 'fill-box', transformOrigin: '50% 100%' }}>

                {/* Pin-boks — centreret på pinX */}
                <rect x={pinX - IW / 2} y={iconY}
                  width={IW} height={IH} rx="9"
                  fill="rgba(6,10,7,0.93)" stroke={col} strokeWidth="2"
                  opacity={done ? 1 : 0.50} />

                {/* Thumbnail hvis media-pin */}
                {thumbSrc ? (
                  <>
                    <defs>
                      <clipPath id={clipId}>
                        <rect x={pinX - IW/2 + 2} y={iconY + 2} width={IW - 4} height={IH - 4} rx="7"/>
                      </clipPath>
                    </defs>
                    <image
                      href={thumbSrc}
                      x={pinX - IW/2 + 2} y={iconY + 2}
                      width={IW - 4} height={IH - 4}
                      clipPath={`url(#${clipId})`}
                      preserveAspectRatio="xMidYMid slice"
                    />
                    {/* Play-ikon overlay for video */}
                    {ms.type === 'video' && (
                      <path
                        d={`M${cx - 6},${cy - 7} L${cx - 6},${cy + 7} L${cx + 9},${cy} Z`}
                        fill="rgba(255,255,255,0.90)"
                      />
                    )}
                  </>
                ) : (
                  <PinIcon type={ms.type} cx={cx} cy={cy} col={col}/>
                )}

                {/* Timestamp-label — centreret på labelX */}
                {tsText && (
                  <g>
                    <rect x={labelX - 16} y={iconY - 15} width={32} height={11} rx="5"
                      fill="rgba(4,8,5,0.85)" stroke="rgba(255,255,255,0.14)" strokeWidth="0.7"/>
                    <text x={labelX} y={iconY - 7} textAnchor="middle"
                      fill="rgba(255,255,255,0.88)" fontSize="8" fontWeight="700"
                      letterSpacing="0.05em"
                      fontFamily="'SF Mono','Courier New',monospace">{tsText}</text>
                  </g>
                )}
              </g>
            </g>
          );
        })}
      </svg>

    </div>
  );
}

export default memo(JourneyTimelineInner);
